import React, { useState } from 'react';
import { Popconfirm, Tooltip, message } from 'antd';
import { DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Avatar from './Avatar';
import { SOURCE_BADGE } from '../constants';
import { getFullName } from '../utils/personUtils';
import { getDownloadUrl } from '../../api/projectManagementApi';
import { formatBytes } from '../../utils/attachmentUtils';
import { useTaskDrawer } from '../TaskDrawerContext';

/** Pick icon name + background colour for file type */
function getFileIconInfo(attachment) {
    const name = (attachment.originalName || '').toLowerCase();
    const mime = (attachment.mimeType || '').toLowerCase();

    if (mime.includes('pdf') || name.endsWith('.pdf'))
        return { icon: 'picture_as_pdf', bg: '#fde8e8', color: '#c0392b', label: 'PDF' };
    if (mime.startsWith('image/') || name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.gif') || name.endsWith('.svg') || name.endsWith('.webp'))
        return { icon: 'image', bg: '#e8f4fd', color: '#2980b9', label: 'IMG' };
    if (mime.includes('word') || name.endsWith('.doc') || name.endsWith('.docx') || name.endsWith('.odt'))
        return { icon: 'description', bg: '#e8edf8', color: '#2b4da0', label: 'DOC' };
    if (mime.includes('sheet') || name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.csv'))
        return { icon: 'table_chart', bg: '#e6f4ee', color: '#1a7a45', label: 'XLS' };
    if (mime.includes('powerpoint') || name.endsWith('.ppt') || name.endsWith('.pptx'))
        return { icon: 'slideshow', bg: '#fef0e6', color: '#c0622b', label: 'PPT' };
    if (mime.includes('zip') || mime.includes('rar') || name.endsWith('.zip') || name.endsWith('.rar') || name.endsWith('.7z') || name.endsWith('.tar') || name.endsWith('.gz'))
        return { icon: 'folder_zip', bg: '#fef9e6', color: '#b7950b', label: 'ZIP' };
    if (mime.includes('video/') || name.endsWith('.mp4') || name.endsWith('.mov') || name.endsWith('.avi') || name.endsWith('.mkv') || name.endsWith('.webm') || name.endsWith('.3gp'))
        return { icon: 'videocam', bg: '#f0e6fe', color: '#7d3c98', label: 'VID' };
    if (mime.includes('audio/') || name.endsWith('.mp3') || name.endsWith('.wav') || name.endsWith('.m4a') || name.endsWith('.ogg') || name.endsWith('.flac') || name.endsWith('.aac'))
        return { icon: 'audio_file', bg: '#fde8f5', color: '#c0407a', label: 'AUD' };
    if (name.endsWith('.txt') || name.endsWith('.md'))
        return { icon: 'article', bg: '#efefef', color: '#555', label: 'TXT' };
    return { icon: 'attach_file', bg: '#efefef', color: '#666', label: 'FILE' };
}

/** Parses "📎 Attached: filename.jpg" style text comments into structured data */
function parseAttachmentText(text) {
    if (!text) return { cleanText: '', attachmentNames: [] };
    const attachmentNames = [];
    const cleanText = text.replace(/(.*?)\s*📎\s*Attached:\s*([^\n]+)/g, (match, prefix, attachmentsStr) => {
        attachmentsStr.split(',').forEach(n => attachmentNames.push(n.trim()));
        return prefix;
    }).trim();
    return { cleanText, attachmentNames };
}

function CommentAttachment({ attachment, taskId }) {
    const [downloading, setDownloading] = useState(false);
    const [viewing, setViewing] = useState(false);
    const { icon, bg, color } = getFileIconInfo(attachment);

    const getUrl = async (disposition = 'inline') => {
        const { data } = await getDownloadUrl(taskId, attachment._id, disposition);
        return data.result.downloadUrl;
    };

    const handleView = async () => {
        setViewing(true);
        try {
            const url = await getUrl('inline');
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
            message.error('Could not open attachment.');
        } finally {
            setViewing(false);
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const url = await getUrl('attachment');
            const link = document.createElement('a');
            link.href = url;
            link.download = attachment.originalName || 'attachment';
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch {
            message.error('Could not download attachment.');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div
            onClick={handleView}
            className="flex items-center gap-2.5 p-2.5 bg-white border border-ed-surface-container-high rounded-xl w-fit cursor-pointer hover:shadow-sm transition-shadow group"
            style={{ maxWidth: 260 }}
        >
            {/* File-type icon badge */}
            <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: bg }}
            >
                <span className="material-symbols-outlined text-[20px]" style={{ color }}>
                    {icon}
                </span>
            </div>

            {/* Name + size */}
            <div className="flex-1 min-w-0">
                <div
                    className="text-xs font-semibold text-ed-on-surface truncate"
                    title={attachment.originalName}
                >
                    {attachment.originalName}
                </div>
                <div className="text-xs text-ed-on-surface-variant mt-0.5">
                    {formatBytes(attachment.size)}
                </div>
            </div>

            {/* Download button */}
            <Tooltip title="Download">
                <button
                    onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                    disabled={downloading || viewing}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-ed-on-surface-variant hover:text-primary p-0.5 flex-shrink-0"
                >
                    {downloading
                        ? <LoadingOutlined style={{ fontSize: 13 }} />
                        : <span className="material-symbols-outlined text-sm">download</span>
                    }
                </button>
            </Tooltip>
        </div>
    );
}

/** Fallback chip for plain-text "📎 Attached: filename" style comments */
function PlainAttachmentChip({ name }) {
    // Guess icon from extension
    const fakeAttachment = { originalName: name, mimeType: '' };
    const { icon, bg, color } = getFileIconInfo(fakeAttachment);
    return (
        <div className="flex items-center gap-2 p-2 bg-white border border-ed-surface-container-high rounded-xl w-fit" style={{ maxWidth: 240 }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bg }}>
                <span className="material-symbols-outlined text-[18px]" style={{ color }}>{icon}</span>
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-ed-on-surface truncate" title={name}>{name}</div>
                <div className="text-xs text-ed-on-surface-variant mt-0.5">Attached file</div>
            </div>
        </div>
    );
}

/** Chip for linked @File project storage references */
function CommentFileReferenceChip({ fileRef, taskId, taskAttachments = [] }) {
    const [actionLoading, setActionLoading] = useState(false);
    const name = fileRef.originalName || fileRef.fileName || 'Referenced File';
    const folderLabel = fileRef.revisionRound || (fileRef.folderCode ? `Folder ${fileRef.folderCode}` : 'Project File');

    // Find if this file also exists as an attachment on the task
    const matchedAtt = taskAttachments.find(
        (a) => (a.originalName || a.fileName || '').trim().toLowerCase() === name.trim().toLowerCase()
    );

    const handleClick = async () => {
        if (fileRef.publicUrl) {
            window.open(fileRef.publicUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (matchedAtt?.publicUrl) {
            window.open(matchedAtt.publicUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (matchedAtt?._id && taskId) {
            setActionLoading(true);
            try {
                const { data } = await getDownloadUrl(taskId, matchedAtt._id, 'inline');
                if (data?.result?.downloadUrl) {
                    window.open(data.result.downloadUrl, '_blank', 'noopener,noreferrer');
                } else {
                    message.info(`File reference: ${name}`);
                }
            } catch {
                message.error('Could not open referenced file.');
            } finally {
                setActionLoading(false);
            }
            return;
        }
        message.info(`File reference: ${name}`);
    };

    return (
        <div
            onClick={handleClick}
            className="inline-flex items-center gap-2 p-2 bg-[#eff4ff] dark:bg-[#1a2538] border border-[#0070F3]/30 dark:border-[#0070F3]/50 rounded-xl text-xs cursor-pointer hover:shadow-sm hover:border-[#0070F3] transition-all group max-w-[280px]"
            title={`Referenced Project File: ${name}`}
        >
            <div className="w-8 h-8 rounded-lg bg-[#0070F3]/15 text-[#0070F3] dark:text-[#60a5fa] flex items-center justify-center flex-shrink-0">
                <span className="material-symbols-outlined text-[18px]">folder_shared</span>
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-[#0b1c30] dark:text-[#eaf1ff] truncate">
                    {name}
                </div>
                <div className="text-[10px] text-[#0070F3] font-medium truncate flex items-center gap-1">
                    <span>@File</span>
                    <span className="opacity-70">• {folderLabel}</span>
                </div>
            </div>
            {actionLoading ? (
                <LoadingOutlined style={{ fontSize: 13, color: '#0070F3' }} />
            ) : (
                <span className="material-symbols-outlined text-sm text-[#727786] group-hover:text-[#0070F3] transition-colors flex-shrink-0">
                    open_in_new
                </span>
            )}
        </div>
    );
}

/** Interactive inline chip for @File[filename] occurrences in comment body */
function InlineFileMentionChip({ fileName, taskAttachments = [], fileReferences = [], taskId }) {
    const [actionLoading, setActionLoading] = useState(false);
    const cleanName = (fileName || '').trim();

    const matchedAtt = taskAttachments.find(
        (a) => (a.originalName || a.fileName || '').trim().toLowerCase() === cleanName.toLowerCase()
    );
    const matchedRef = (fileReferences || []).find(
        (r) => (r.originalName || r.fileName || '').trim().toLowerCase() === cleanName.toLowerCase()
    );

    const fileMeta = matchedAtt || matchedRef || { originalName: cleanName, mimeType: '' };
    const { icon, bg, color } = getFileIconInfo(fileMeta);

    const handleView = async (e) => {
        e.stopPropagation();
        if (matchedAtt?.publicUrl) {
            window.open(matchedAtt.publicUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (matchedRef?.publicUrl) {
            window.open(matchedRef.publicUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (matchedAtt?._id && taskId) {
            setActionLoading(true);
            try {
                const { data } = await getDownloadUrl(taskId, matchedAtt._id, 'inline');
                if (data?.result?.downloadUrl) {
                    window.open(data.result.downloadUrl, '_blank', 'noopener,noreferrer');
                } else {
                    message.info(`File: ${cleanName}`);
                }
            } catch {
                message.error('Could not open referenced file.');
            } finally {
                setActionLoading(false);
            }
            return;
        }
        message.info(`Referenced file: ${cleanName}`);
    };

    const handleDownload = async (e) => {
        e.stopPropagation();
        if (matchedAtt?._id && taskId) {
            setActionLoading(true);
            try {
                const { data } = await getDownloadUrl(taskId, matchedAtt._id, 'attachment');
                const url = data?.result?.downloadUrl || matchedAtt.publicUrl;
                if (url) {
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = cleanName;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                }
            } catch {
                message.error('Could not download file.');
            } finally {
                setActionLoading(false);
            }
            return;
        }
        if (matchedAtt?.publicUrl || matchedRef?.publicUrl) {
            const url = matchedAtt?.publicUrl || matchedRef?.publicUrl;
            const link = document.createElement('a');
            link.href = url;
            link.download = cleanName;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            link.remove();
            return;
        }
        message.info(`File reference: ${cleanName}`);
    };

    return (
        <span
            onClick={handleView}
            className="inline-flex items-center gap-1.5 px-2 py-0.5 mx-1 my-0.5 rounded-lg border border-blue-200 dark:border-blue-900/60 bg-blue-50/90 dark:bg-[#1a2538] hover:bg-blue-100/90 dark:hover:bg-[#22324d] text-blue-800 dark:text-blue-300 text-xs font-medium cursor-pointer shadow-xs transition-all align-middle group/filechip select-none"
            title={`Referenced File: ${cleanName} (Click to view / download)`}
        >
            <span className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bg }}>
                <span className="material-symbols-outlined text-[14px]" style={{ color }}>
                    {icon}
                </span>
            </span>
            <span className="truncate max-w-[220px] font-semibold">{cleanName}</span>
            <span className="text-[10px] text-blue-600 dark:text-blue-400 font-bold px-1 py-0.2 bg-blue-100 dark:bg-blue-900/40 rounded">
                @File
            </span>
            <Tooltip title="Download file">
                <button
                    type="button"
                    onClick={handleDownload}
                    disabled={actionLoading}
                    className="text-slate-400 hover:text-blue-600 dark:hover:text-blue-300 p-0.5 transition-colors flex items-center"
                >
                    {actionLoading ? (
                        <LoadingOutlined style={{ fontSize: 11 }} />
                    ) : (
                        <span className="material-symbols-outlined text-[13px]">download</span>
                    )}
                </button>
            </Tooltip>
        </span>
    );
}

/** Renders comment body parsing @File[...] tokens into clickable, interactive chips */
function renderCommentBody(text, taskAttachments, fileReferences, taskId) {
    if (!text) return null;
    const fileMentionRegex = /@File\[([^\]]+)\]/g;
    if (!fileMentionRegex.test(text)) {
        return text;
    }
    fileMentionRegex.lastIndex = 0;

    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = fileMentionRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }
        const fileName = match[1];
        parts.push(
            <InlineFileMentionChip
                key={`file-mention-${match.index}`}
                fileName={fileName}
                taskAttachments={taskAttachments}
                fileReferences={fileReferences}
                taskId={taskId}
            />
        );
        lastIndex = fileMentionRegex.lastIndex;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts;
}

export default function CommentItem({ comment, currentAdminId, onDelete, taskId }) {
    const { task } = useTaskDrawer();
    const taskAttachments = task?.attachments || [];

    const authorName = getFullName(comment.author) || 'Unknown';
    const badge = SOURCE_BADGE[comment.source] || SOURCE_BADGE.manual;
    const isOwn = comment.author?._id === currentAdminId;
    const canDelete = isOwn && !comment.isSystemGenerated;

    // Structured attachments (API objects)
    const hasAttachments = (comment.attachments?.length || 0) > 0;

    // Parse plain-text fallback attachments (📎 Attached: filename)
    const { cleanText, attachmentNames } = parseAttachmentText(comment.text);
    const hasPlainAttachments = attachmentNames.length > 0;

    return (
        <div className="flex gap-3 group">
            <Avatar person={comment.author} name={authorName} size={32} />
            <div className="flex-1 min-w-0 space-y-1">
                {/* Name + timestamp row */}
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-ed-on-surface">{authorName}</span>
                        {badge.label && (
                            <span className={`text-sm font-bold px-1.5 py-0.5 rounded ${badge.color}`}>
                                {badge.label}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <Tooltip title={dayjs(comment.createdAt).format('dddd, MMMM D, YYYY · h:mm:ss A')}>
                            <span className="text-xs font-bold text-ed-on-surface-variant cursor-default">
                                {dayjs(comment.createdAt).format('MMM D, YYYY · h:mm A')}
                            </span>
                        </Tooltip>
                        {canDelete && (
                            <Popconfirm
                                title="Delete this comment?"
                                onConfirm={() => onDelete(comment._id)}
                                okText="Delete"
                                okButtonProps={{ danger: true, size: 'small' }}
                                cancelText="Cancel"
                                placement="topRight"
                            >
                                <button className="text-ed-on-surface-variant hover:text-error transition-colors opacity-0 group-hover:opacity-100">
                                    <DeleteOutlined style={{ fontSize: 12 }} />
                                </button>
                            </Popconfirm>
                        )}
                    </div>
                </div>

                {/* Comment bubble — grey background, rounded except top-left corner */}
                {(cleanText || (!hasPlainAttachments && !hasAttachments)) && (
                    <p className="text-sm text-ed-on-surface bg-ed-surface-container-low p-3 rounded-xl rounded-tl-none leading-relaxed whitespace-pre-wrap">
                        {renderCommentBody(cleanText || comment.text, taskAttachments, comment.fileReferences, taskId)}
                    </p>
                )}

                {/* Structured attachment cards (rich API objects) */}
                {hasAttachments && (
                    <div className="flex flex-wrap gap-2 pt-1">
                        {comment.attachments.map((att) => (
                            <CommentAttachment key={att._id} attachment={att} taskId={taskId} />
                        ))}
                    </div>
                )}

                {/* Fallback plain-text attachment chips */}
                {hasPlainAttachments && (
                    <div className="flex flex-wrap gap-2 pt-1">
                        {attachmentNames.map((name, i) => {
                            const matched = taskAttachments.find(att => att.originalName === name);
                            if (matched) {
                                return <CommentAttachment key={matched._id || i} attachment={matched} taskId={taskId} />;
                            }
                            return <PlainAttachmentChip key={i} name={name} />;
                        })}
                    </div>
                )}

                {/* Structured @File project references */}
                {comment.fileReferences && comment.fileReferences.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                        {comment.fileReferences.map((ref, idx) => (
                            <CommentFileReferenceChip key={ref._id || idx} fileRef={ref} taskId={taskId} taskAttachments={taskAttachments} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
